import React, { useCallback } from "react";
import { useSalon } from "../../context/SalonContext";
import { SalonContextType } from "../../types/context";
import usePrintDocument from "../../hooks/usePrintDocument";
import GiftCardCertificate, {
  GiftCardCertificateProps,
} from "./GiftCardCertificate";

/**
 * The certificate fields a caller supplies. Business identity
 * (`businessName` / `businessLogoUrl`) is injected by the hook from
 * `SalonContext`, so no surface has to thread it, and `variant` is fixed to
 * `"print"` for the hidden copy.
 */
export type GiftCardPrintData = Omit<
  GiftCardCertificateProps,
  "businessName" | "businessLogoUrl" | "variant"
>;

interface UseGiftCardPrint {
  /** Print one gift-card certificate — mounts a single hidden print copy and
   *  opens the browser/OS print dialog once it (and the logo) are ready. */
  printCard: (data: GiftCardPrintData) => void;
  /** Render this ONCE at the surface root. It is the single hidden `.printOnly`
   *  certificate — never render it per-item, or every copy would print at once. */
  certificate: React.ReactNode;
}

/**
 * Reusable gift-card printing for every surface (post-checkout modal, Sales
 * drawer, client Credits card). Instantiate it ONCE per surface and render
 * `certificate` a single time; call `printCard(data)` to print. The print
 * mechanics (single mount, Portal, logo gate, `afterprint` cleanup) live in
 * `usePrintDocument`.
 */
export default function useGiftCardPrint(): UseGiftCardPrint {
  const { businessInfo } = useSalon() as SalonContextType;

  const businessName = businessInfo?.salon_name || "";
  const businessLogoUrl = businessInfo?.logo_url || null;

  const renderCertificate = useCallback(
    (data: GiftCardPrintData) => (
      <GiftCardCertificate
        variant="print"
        businessName={businessName}
        businessLogoUrl={businessLogoUrl}
        {...data}
      />
    ),
    [businessName, businessLogoUrl],
  );

  const { print, printNode } = usePrintDocument(renderCertificate, {
    waitForImageUrl: businessLogoUrl,
  });

  return { printCard: print, certificate: printNode };
}
